import {Box, withShade} from '@dropbox/dig-foundations';
import {ComponentProps, ReactNode} from 'react';

type BoxSpacing = ComponentProps<typeof Box>['paddingY'];
type BorderColor = ComponentProps<typeof Box>['borderColor'];

/**
 * Standard row inside a dashboard tile's scrolling list (News, New arrivals,
 * Workstreams, Agenda, …). Applies the shared hover-shade plus the horizontal
 * "bleed" (`marginInline: -8`) so the hover/active background fills out to the
 * tile's inner edge while the row content stays aligned with the tile header.
 *
 * Pair this with a scroller that reserves the bleed room
 * (`marginInline: -8` + `paddingInline: 8`) — otherwise `overflowY: auto`
 * forces `overflowX: auto` (per the CSS spec) and clips the sideways shade.
 */
export const TileRow = ({
  children,
  paddingY = '6',
  interactive = true,
  accent,
  faded = false,
}: {
  children: ReactNode;
  /** Vertical padding on the spacing scale (defaults to `6`). */
  paddingY?: BoxSpacing;
  /** Adds the pointer cursor for click/expand rows (defaults to `true`). */
  interactive?: boolean;
  /** Thin left accent bar color */
  accent?: BorderColor;
  /** Dims the whole row (e.g. past events that are over). */
  faded?: boolean;
}) => {
  return (
    <Box
      {...withShade({
        style: {
          marginInline: -8,
          ...(faded ? {opacity: 0.6} : {}),
          ...(interactive ? {cursor: 'pointer'} : {}),
        },
      })}
      borderLeft="Solid"
      borderColor={accent ?? 'transparent'}
      borderWidth="2"
      paddingX="8"
      borderRadius="Medium"
      borderTopLeftRadius={accent ? 'None' : undefined}
      borderBottomLeftRadius={accent ? 'None' : undefined}
      paddingY={paddingY}
    >
      {children}
    </Box>
  );
};
